// src/pages/About.jsx
import React from "react";
import {
  FaShip,
  FaPlane,
  FaTruck,
  FaMapMarkerAlt,
  FaClock,
  FaUsers,
  FaAward,
  FaShieldAlt,
  FaCheck,
  FaGlobe,
  FaPhone,
  FaEnvelope,
  FaArrowRight,
} from "react-icons/fa";
import "./About.css";

const About = () => {
  const stats = [
    { icon: <FaClock />, number: "10+", label: "Years Experience", suffix: "" },
    {
      icon: <FaShip />,
      number: "5000+",
      label: "Containers",
      suffix: "Shipped",
    },
    { icon: <FaUsers />, number: "200+", label: "Happy Clients", suffix: "" },
    { icon: <FaGlobe />, number: "25+", label: "Countries", suffix: "Served" },
  ];

  const locations = [
    {
      name: "Nairobi ICD",
      description: "Inland Container Depot",
      icon: <FaMapMarkerAlt />,
    },
    {
      name: "Mombasa Port",
      description: "Main Seaport Operations",
      icon: <FaShip />,
    },
    { name: "JKIA", description: "Air Cargo Hub", icon: <FaPlane /> },
  ];

  const services = [
    { name: "Sea Freight", icon: <FaShip />, color: "#3B82F6" },
    { name: "Air Freight", icon: <FaPlane />, color: "#10B981" },
    { name: "Road Transport", icon: <FaTruck />, color: "#F59E0B" },
    { name: "Customs Clearance", icon: <FaShieldAlt />, color: "#8B5CF6" },
  ];

  const principles = [
    {
      icon: <FaShieldAlt />,
      title: "Compliance",
      description:
        "100% KRA-aligned processes, accurate HS classification, and audit-ready documentation.",
      features: [
        "KRA Ready Entries",
        "Accurate Tariff Classification",
        "Audit-Ready Documentation",
      ],
    },
    {
      icon: <FaClock />,
      title: "Speed",
      description:
        "Proactive workflows that cut dwell time and keep your cargo moving efficiently.",
      features: [
        "1-3 Day Clearance",
        "Proactive Document Checks",
        "Streamlined Workflows",
      ],
    },
    {
      icon: <FaAward />,
      title: "Transparency",
      description:
        "Clear quotes, real-time tracking, and honest communication at every step.",
      features: ["Real-time Tracking", "Clear Pricing", "Milestone Updates"],
    },
  ];

  const teamFeatures = [
    "Licensed KRA customs brokerage",
    "Real-time GPS tracking and milestone updates",
    "Over 50,000 sq ft of modern warehousing",
    "Modern fleet of 20+ vehicles",
    "Expertise in perishables and pharmaceuticals",
    "ERP-ready systems integration",
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">Since 2015</div>
              <h1>Your Premier Logistics Partner in Kenya</h1>
              <p className="hero-description">
                Falmouth Global Solutions is a top-rated logistics company
                providing comprehensive shipping services, customs clearing, and
                supply chain solutions in Kenya and beyond since 2015.
              </p>
              <p className="hero-subtitle">
                Recognized as one of the leading customs clearing agents and
                freight forwarders in Kenya, offering seamless sea freight, air
                freight, and road transport services.
              </p>
              <div className="hero-cta">
                <a href="/services" className="btn btn-primary">
                  Explore Our Services <FaArrowRight />
                </a>
                <a href="/contact" className="btn btn-secondary">
                  Get In Touch
                </a>
              </div>
            </div>
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-icon">{stat.icon}</div>
                  <div className="stat-content">
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                    {stat.suffix && (
                      <div className="stat-suffix">{stat.suffix}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="what-we-do">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Our Expertise</div>
            <h2>What We Do Best</h2>
            <p>Specializing in smart cargo movement with precision and care</p>
          </div>

          <div className="services-overview">
            <div className="services-grid">
              {services.map((service, index) => (
                <div key={index} className="service-item">
                  <div
                    className="service-icon-wrapper"
                    style={{
                      backgroundColor: `${service.color}20`,
                      borderColor: service.color,
                    }}
                  >
                    <div
                      className="service-icon"
                      style={{ color: service.color }}
                    >
                      {service.icon}
                    </div>
                  </div>
                  <h4>{service.name}</h4>
                </div>
              ))}
            </div>

            <div className="features-list">
              <h3>Comprehensive Logistics Solutions</h3>
              <div className="features-grid">
                {teamFeatures.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <FaCheck className="check-icon" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="locations-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Our Reach</div>
            <h2>Strategic Operating Locations</h2>
            <p>
              Serving key logistics hubs across Kenya for efficient cargo
              handling
            </p>
          </div>

          <div className="locations-grid">
            {locations.map((location, index) => (
              <div key={index} className="location-card">
                <div className="location-icon">{location.icon}</div>
                <h3>{location.name}</h3>
                <p>{location.description}</p>
                <div className="location-badge">Operational</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Promise Section */}
      <section className="promise-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Our Commitment</div>
            <h2>Our Promise to You</h2>
            <p>
              At Falmouth Global Solutions, we live by three core principles
              that define our service excellence
            </p>
          </div>

          <div className="principles-grid">
            {principles.map((principle, index) => (
              <div key={index} className="principle-card">
                <div className="principle-icon">{principle.icon}</div>
                <h3>{principle.title}</h3>
                <p>{principle.description}</p>
                <div className="principle-features">
                  {principle.features.map((feature, idx) => (
                    <div key={idx} className="feature-tag">
                      <FaCheck className="feature-check" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section">
        <div className="container">
          <div className="trust-content">
            <div className="trust-text">
              <h2>Trusted by Industry Leaders</h2>
              <p>
                Since 2015, manufacturers, retailers, NGOs, mining companies,
                healthcare providers, and fast-growing startups have trusted us
                to deliver on time, on budget, and without surprises.
              </p>
              <div className="trust-stats">
                <div className="trust-stat">
                  <div className="trust-number">100%</div>
                  <div className="trust-label">KRA Compliance</div>
                </div>
                <div className="trust-stat">
                  <div className="trust-number">24/7</div>
                  <div className="trust-label">Customer Support</div>
                </div>
                <div className="trust-stat">
                  <div className="trust-number">50+</div>
                  <div className="trust-label">Expert Team</div>
                </div>
              </div>
            </div>
            <div className="trust-cta">
              <h3>Let Us Simplify Your Logistics</h3>
              <p>
                Help your business grow faster in East Africa with our reliable
                solutions.
              </p>
              <div className="cta-buttons">
                <a href="tel:+254723335043" className="btn btn-primary">
                  <FaPhone /> Call +254 723 335 043
                </a>
                <a
                  href="mailto:info@falmouth.co.ke"
                  className="btn btn-secondary"
                >
                  <FaEnvelope /> Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <div className="final-cta-content">
            <h2>Falmouth Global Solutions – Moving Kenya Forward</h2>
            <p>
              Your trusted partner for seamless logistics and supply chain
              solutions across East Africa
            </p>
            <a href="/services" className="btn btn-primary">
              Discover Our Services <FaArrowRight />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
